import BlogArticle from '@/components/feature/BlogArticle';

const props = {
  "slug": "fenetre-qui-ferme-mal",
  "category": "Fenêtre",
  "seoTitle": "Fenêtre qui ferme mal : crémone, réglage et solutions",
  "metaDescription": "Fenêtre qui ferme mal ou qui coince à Lyon ? Crémone, dilatation, gonds : les causes réelles, ce qui se règle, ce qui se remplace, et quand appeler un pro.",
  "h1": "Fenêtre qui ferme mal : crémone, réglage et solutions",
  "datePublished": "2026-07-20",
  "dateLabel": "20 juillet 2026",
  "readingTime": "6 min",
  "heroImage": "/images/dfpro-fenetre-bloquee-2026.webp",
  "heroAlt": "Fenêtre qui ferme mal à Lyon, réglage et réparation",
  "ctaTitle": "Poignée dure, fenêtre qui frotte ?",
  "ctaText": "DFPRO Lyon règle ou remplace. Devis gratuit, garantie 10 ans.",
  "intro": [
    "Une fenêtre qui ferme mal, c'est plus qu'un désagrément : elle laisse passer l'air, le bruit et l'humidité, dégrade votre confort et fait grimper la facture de chauffage. Pourtant, le problème vient rarement de la vitre elle-même — c'est presque toujours la quincaillerie ou le réglage qui est en cause.",
    "Bonne nouvelle : beaucoup de ces problèmes se corrigent, parfois par un simple réglage, parfois par le remplacement d'une pièce peu coûteuse. Voici les causes les plus fréquentes, ce que vous pouvez tenter, et les cas où l'intervention d'un professionnel s'impose."
  ],
  "sections": [
    {
      "h2": "La crémone : la cause n°1",
      "body": [
        "La <strong>crémone</strong> est le mécanisme de fermeture, actionné par la poignée : en tournant, elle déplace des tringles et des galets qui viennent verrouiller le vantail contre le dormant. Avec l'usage, elle peut se gripper, se dérégler ou casser.",
        "Symptômes typiques : la poignée devient dure, bloque à mi-course, ou <strong>tourne dans le vide</strong> sans plus rien verrouiller. Dans ce dernier cas, le mécanisme interne est cassé et le <a href=\"/changer-cremone-fenetre-lyon/\" class=\"text-primary-600 font-medium underline\">remplacement de la crémone</a> règle définitivement le problème."
      ]
    },
    {
      "h2": "La dilatation et les variations de température",
      "body": [
        "Le PVC et l'aluminium se dilatent avec la chaleur. À Lyon, lors des fortes chaleurs d'été, un vantail déjà juste peut se mettre à frotter ou à coincer, puis retrouver son jeu quand la température baisse. Le bois, lui, gonfle avec l'humidité.",
        "Si votre fenêtre ne ferme mal qu'à certaines saisons, la dilatation est probablement en jeu. Un réglage des ferrures adapté permet de retrouver un fonctionnement correct toute l'année."
      ]
    },
    {
      "h2": "Gonds, paumelles et réglages",
      "body": [
        "Des gonds ou paumelles desserrés ou déréglés font que le vantail s'affaisse, frotte en bas ou bâille en haut. Un réglage précis — jeu, hauteur, compression du joint — rétablit une fermeture nette.",
        "Sur une fenêtre oscillo-battante, le <a href=\"/reglage-oscillo-battant-lyon/\" class=\"text-primary-600 font-medium underline\">réglage de l'oscillo-battant</a> est fréquent : c'est le mécanisme qui permet à la fenêtre de s'ouvrir à la française ou de basculer en soufflet, et il se dérègle avec le temps."
      ]
    },
    {
      "h2": "Les joints d'étanchéité",
      "body": [
        "Un joint durci, écrasé ou fissuré ne comprime plus correctement : la fenêtre semble fermée mais laisse passer l'air. Le <a href=\"/changer-joint-fenetre-lyon/\" class=\"text-primary-600 font-medium underline\">remplacement du joint</a> restaure l'étanchéité, supprime les courants d'air et améliore l'isolation thermique et phonique.",
        "C'est une intervention simple et peu coûteuse, souvent négligée, qui change pourtant nettement le confort en hiver."
      ]
    },
    {
      "h2": "Ce qui se règle, ce qui se remplace, et quand appeler un pro",
      "body": [
        "Beaucoup de problèmes se corrigent par un réglage des ferrures. Mais quand une pièce est usée — crémone, poignée, joint, galet — le réglage ne tient pas dans la durée et le remplacement est préférable.",
        "Si la fenêtre ne se verrouille plus, si la poignée est cassée, ou si le vantail continue de frotter malgré vos tentatives, faites intervenir un professionnel avant d'endommager la quincaillerie. DFPRO Lyon réalise la <a href=\"/reparation-fenetre-lyon/\" class=\"text-primary-600 font-medium underline\">réparation de fenêtre</a> sur PVC, aluminium et bois, toutes marques, y compris les menuiseries anciennes fréquentes dans certains quartiers lyonnais. Devis gratuit, garantie 10 ans, intervention sous 24-48h."
      ]
    }
  ],
  "faq": [
    {
      "q": "Ma poignée de fenêtre tourne dans le vide, que faire ?",
      "a": "C'est le symptôme classique d'une crémone cassée : le mouvement de la poignée n'entraîne plus le mécanisme de verrouillage. Le remplacement de la crémone résout le problème. DFPRO Lyon intervient toutes marques, au 09 80 80 36 84."
    },
    {
      "q": "Une fenêtre qui ferme mal, est-ce juste un réglage ?",
      "a": "Souvent oui, surtout si elle frotte ou ne comprime plus le joint : un réglage des ferrures suffit. Mais si une pièce est usée (crémone, joint, galet), le réglage ne tient pas durablement et un remplacement est préférable."
    },
    {
      "q": "Pourquoi ma fenêtre ne coince qu'en été ?",
      "a": "C'est la dilatation : le PVC et l'aluminium gonflent avec la chaleur, et un vantail déjà juste se met à frotter aux fortes températures. Un réglage adapté des ferrures permet de retrouver un fonctionnement correct toute l'année."
    },
    {
      "q": "Réparez-vous les fenêtres en bois anciennes ?",
      "a": "Oui, nous intervenons sur les fenêtres PVC, aluminium et bois, y compris les menuiseries anciennes que l'on trouve dans de nombreux immeubles lyonnais."
    }
  ],
  "related": [
    {
      "title": "Double vitrage embué : pourquoi et comment y remédier",
      "href": "/blog/double-vitrage-embue/"
    },
    {
      "title": "Baie vitrée qui coince : roulettes, rail et réglage",
      "href": "/blog/baie-vitree-qui-coince/"
    }
  ]
};

export default function FenetreQuiFermeMal() {
  return <BlogArticle {...props} />;
}
